import AddIcon from '@mui/icons-material/Add';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import { ButtonBase, Stack, Typography, useTheme } from '@mui/material';
import { ResParticipant } from 'features/lead/lead-management/lead-questionnaire-details/lead-questionnaire-details.types';
import { Perspectives } from 'features/lead/lead-questionnaire/lead-questionnaire.types';
import PaiCheckbox from 'features/ui/base-components/pai-checkbox';
import PaiMenuItem from 'features/ui/base-components/pai-menu-item';
import PaiSelect from 'features/ui/base-components/pai-select';
import React from 'react';
import { makeStyles } from 'tss-react/mui';

interface Props {
  participant: ResParticipant;
  isEven: boolean;

  handleEditParticipantIsSuspended(id: string): (event: React.ChangeEvent<HTMLInputElement>) => void;

  copyToClipboard(participant: ResParticipant): () => void;

  getRealPerspective(id: string): string;

  handleEditParticipantRole(id: string): (event: React.ChangeEvent<HTMLInputElement>) => void;
}

const ParticipantItem: React.FC<Props> = ({
  participant,
  isEven,
  handleEditParticipantIsSuspended,
  copyToClipboard,
  getRealPerspective,
  handleEditParticipantRole,
}) => {
  const { classes, cx } = useStyles({ isEven });
  const theme = useTheme();

  return (
    <Stack className={classes.participant} direction={'row'} alignItems={'center'} justifyContent={'space-between'} gap={2}>
      <Stack direction={'row'} alignItems={'center'} gap={1} style={{ flexBasis: '50%' }}>
        <PaiCheckbox
          checked={!participant.isSuspended}
          onChange={handleEditParticipantIsSuspended(participant.id)}
          disabled={participant.perspective === 'self'}
        />
        {!participant.completed ? (
          <ButtonBase className={classes.button} onClick={copyToClipboard(participant)}>
            <Stack direction={'row'} alignItems={'center'} gap={1}>
              <Typography className={classes.buttonText} style={{ textTransform: 'capitalize' }}>
                {participant.name}
              </Typography>
              <FileCopyIcon className={classes.icon} />
            </Stack>
          </ButtonBase>
        ) : participant.completed && participant.perspective !== 'self' ? (
          <ButtonBase className={classes.button} onClick={copyToClipboard(participant)}>
            <Stack direction={'row'} alignItems={'center'} gap={1}>
              <Typography className={classes.buttonText} style={{ textTransform: 'capitalize' }}>
                {participant.name}
              </Typography>
              <AddIcon className={classes.icon} />
            </Stack>
          </ButtonBase>
        ) : (
          <Typography className={classes.text} style={{ textTransform: 'capitalize' }}>
            {participant.name}
          </Typography>
        )}
      </Stack>

      <div style={{ flexBasis: '30%' }}>
        {participant.perspective === 'self' ? (
          <Typography className={cx(classes.text, 'perspective')}>{getRealPerspective(participant.perspective)}</Typography>
        ) : (
          <PaiSelect
            value={participant.perspective}
            disableBorder={true}
            backgroundColor={isEven ? theme.palette.backgrounds.main : '#FFF'}
            dense={true}
            onChange={handleEditParticipantRole(participant.id)}
          >
            <PaiMenuItem value={Perspectives.BOSS}>Führungskraft</PaiMenuItem>
            <PaiMenuItem value={Perspectives.COLLEAGUE}>Kolleg*in</PaiMenuItem>
            <PaiMenuItem value={Perspectives.EMPLOYEE}>Mitarbeiter*in</PaiMenuItem>
            <PaiMenuItem value={Perspectives.OTHER}>Andere</PaiMenuItem>
          </PaiSelect>
        )}
      </div>

      <Typography className={cx(classes.text, participant.completed ? 'completed' : 'not-completed')} style={{ flexBasis: '20%' }}>
        {participant.completed ? 'abgeschlossen' : 'ausstehend'}
      </Typography>
    </Stack>
  );
};

const useStyles = makeStyles<{ isEven: boolean }>()((theme, { isEven }) => ({
  participant: {
    position: 'relative',
    width: '100%',
    padding: '8px 16px',
    background: isEven ? '#FFF' : theme.palette.backgrounds.main,
    minHeight: '47px',
  },
  text: {
    fontSize: theme.typography.pxToRem(13),
    color: theme.palette.greys.medium,
    '&.perspective': {
      padding: '4px 6px',
    },
    '&.not-completed': {
      color: theme.palette.warning.main,
      textAlign: 'right',
    },
    '&.completed': {
      color: theme.palette.success.main,
      textAlign: 'right',
    },
    '&.link': {
      textDecoration: 'underline',
      '&:hover': {
        textDecoration: 'underline',
        color: theme.palette.secondary.main,
      },
    },
  },
  button: {
    color: theme.palette.primary.main,
    '&:hover': {
      color: theme.palette.secondary.main,
    },
  },
  buttonText: {
    fontSize: theme.typography.pxToRem(13),
    textDecoration: 'underline',
    textUnderlineOffset: '4px',
    textTransform: 'lowercase',
    '&.disabled': {
      color: theme.palette.greys.light,
    },
  },
  icon: {
    fontSize: theme.typography.pxToRem(16),
  },
}));

export default ParticipantItem;
